'use client'

import {
  Button,
  Column,
  Grid,
  StructuredListBody,
  StructuredListCell,
  StructuredListRow,
  StructuredListWrapper,
  Tile,
} from '@carbon/react'
import type { CatalogItem } from '@/lib/catalog'
import { publicPagesCopy } from '@/lib/public-pages-i18n'

type PanelDemo = {
  name: 'cPanel' | 'DirectAdmin'
  href: string
  logo: string
  description: string
  credentials?: Array<{
    label: string
    value: string
  }>
}

function panelDemoFor(item: CatalogItem): PanelDemo | null {
  const pricingTitles = item.pricing?.map(({ title }) => title) ?? []

  if (pricingTitles.includes('cPanel shared hosting plans')) {
    return {
      name: 'cPanel',
      href: 'https://trycpanel.net/',
      logo: '/platform-logos/cpanel.svg',
      description: 'Open the cPanel demo environment and explore the control panel before choosing a hosting plan.',
    }
  }

  if (pricingTitles.includes('DirectAdmin shared hosting plans')) {
    return {
      name: 'DirectAdmin',
      href: 'https://demo.directadmin.com:2222/evo',
      logo: '/platform-logos/directadmin.svg',
      description: 'Explore DirectAdmin Evolution using either the user or reseller demo account.',
      credentials: [
        { label: 'User account', value: 'demouser' },
        { label: 'Reseller account', value: 'demoreseller' },
        { label: 'Password', value: 'demo' },
      ],
    }
  }

  return null
}

export function PanelDemoSection({ item, locale = 'en' }: { item: CatalogItem; locale?: string }) {
  const demo = panelDemoFor(item)
  const copy = publicPagesCopy(locale)

  if (!demo) return null

  return (
    <section className="section catalog-demo" aria-labelledby="catalog-demo-title">
      <Grid>
        <Column sm={4} md={4} lg={5}>
          <p className="section-label">{copy.panelDemo}</p>
          <h2 id="catalog-demo-title" className="section__title">{copy.tryPanel.replace('{{panel}}', demo.name)}</h2>
        </Column>
        <Column sm={4} md={4} lg={{ span: 10, offset: 1 }}>
          <Tile className="catalog-demo__tile">
            <div className="catalog-demo__summary">
              <img className="catalog-demo__logo" src={demo.logo} alt={`${demo.name} logo`} />
              <div>
                <p className="catalog-demo__name">{demo.name}</p>
                <p className="catalog-demo__description">{copy.openPanelDemo}</p>
              </div>
            </div>

            <Button
              className="catalog-demo__button"
              href={demo.href}
              target="_blank"
              rel="noreferrer"
              kind="tertiary"
            >
              <span className="catalog-demo__button-content">
                <img src={demo.logo} alt="" aria-hidden="true" />
                <span>{copy.demo}</span>
              </span>
            </Button>

            {demo.credentials ? (
              <div className="catalog-demo__credentials">
                <p className="catalog-demo__credentials-title">{copy.demoCredentials}</p>
                <StructuredListWrapper isCondensed aria-label={copy.demoCredentials}>
                  <StructuredListBody>
                    {demo.credentials.map(({ label, value }) => (
                      <StructuredListRow key={label}>
                        <StructuredListCell>{label}</StructuredListCell>
                        <StructuredListCell>
                          <code className="catalog-demo__credential-value">{value}</code>
                        </StructuredListCell>
                      </StructuredListRow>
                    ))}
                  </StructuredListBody>
                </StructuredListWrapper>
                <p className="catalog-demo__credentials-note">{copy.demoPasswordNote}</p>
              </div>
            ) : null}
          </Tile>
        </Column>
      </Grid>
    </section>
  )
}
